<template>
    <v-layout row wrap>
        <v-flex xs10 offset-xs1 lg8 offset-lg2>
            <v-form ref="form" @submit.prevent="submit">
                <confirm-dialogue ref="confirmDialogue"></confirm-dialogue>
                <v-card class="pa-3" v-if="!loading">
                    <v-card-text>
                        <v-alert
                            color="warning"
                            icon="warning"
                            value="true"
                            class="mb-3"
                            v-if="form.isInUse"
                        >
                            {{ $t("message.info.systemInUse") }}
                        </v-alert>

                        <div class="subheading mb-3">
                            {{ $t("label.basicInfo") }}
                        </div>
                        <v-layout row>
                            <v-flex xs3 lg2>
                                <label class="form-label"
                                    >{{ $t("label.acronym") }}*</label
                                >
                            </v-flex>
                            <v-flex xs2 lg2>
                                <v-text-field
                                    :disabled="disabled"
                                    v-model="form.acronym"
                                    :rules="validation.text"
                                    :maxlength="constants.LENGTH_SYSTEM_ACRONYM"
                                ></v-text-field>
                            </v-flex>
                        </v-layout>
                        <v-layout row>
                            <v-flex xs3 lg2>
                                <label class="form-label"
                                    >{{ $t("label.name") }}*</label
                                >
                            </v-flex>
                            <v-flex xs9 lg10>
                                <v-text-field
                                    :disabled="disabled"
                                    v-model="form.name"
                                    :rules="validation.text"
                                    :maxlength="constants.LENGTH_SYSTEM_NAME"
                                    :counter="constants.LENGTH_SYSTEM_NAME"
                                ></v-text-field>
                            </v-flex>
                        </v-layout>
                        <v-layout row>
                            <v-flex xs3 lg2>
                                <label class="form-label"
                                    >{{ $t("label.office") }}*</label
                                >
                            </v-flex>
                            <v-flex xs7 lg6>
                                <v-select
                                    autocomplete
                                    :disabled="disabled"
                                    v-bind:items="offices"
                                    v-model="form.office.id"
                                    :rules="validation.required"
                                    :no-data-text="$t('message.warning.noData')"
                                ></v-select>
                            </v-flex>
                        </v-layout>
                        <v-layout row>
                            <v-flex xs3 lg2>
                                <label class="form-label"
                                    >{{ $t("label.contract") }}*</label
                                >
                            </v-flex>
                            <v-flex xs9 lg10>
                                <v-select
                                    autocomplete
                                    :disabled="disabled"
                                    v-bind:items="contracts"
                                    v-model="form.contract.id"
                                    clearable
                                    :no-data-text="$t('message.warning.noData')"
                                    :maxlength="constants.LENGTH_CONTRACT"
                                    :rules="validation.required"
                                ></v-select>
                            </v-flex>
                        </v-layout>
                        <v-layout row v-if="!!this.form.contract.id">
                            <v-flex xs3 lg2>
                                <label class="form-label"
                                    >{{ $t("label.contractItem") }}*</label
                                >
                            </v-flex>
                            <v-flex xs7 lg6>
                                <v-select
                                    autocomplete
                                    :disabled="disabled"
                                    v-bind:items="contractItems"
                                    v-model="form.contractItem.id"
                                    :rules="validation.required"
                                    :no-data-text="$t('message.warning.noData')"
                                    :maxlength="constants.LENGTH_CONTRACT_ITEM"
                                ></v-select>
                            </v-flex>
                        </v-layout>
                        <v-layout row>
                            <v-flex xs3 lg2>
                                <label class="form-label"
                                    >{{ $t("label.supplier") }}*</label
                                >
                            </v-flex>
                            <v-flex xs9 lg10>
                                <v-select
                                    :disabled="disabled"
                                    v-bind:items="suppliers"
                                    v-model="form.supplier.id"
                                    v-on:change="clearContractor"
                                    autocomplete
                                    :rules="validation.required"
                                    :no-data-text="$t('message.warning.noData')"
                                >
                                </v-select>
                            </v-flex>
                        </v-layout>
                        <v-layout row>
                            <v-flex xs3 lg2>
                                <label class="form-label"
                                    >{{ $t("label.contractor") }}*</label
                                >
                            </v-flex>
                            <v-flex xs9 lg10>
                                <v-select
                                    :disabled="disabled || noOfficeDisabled"
                                    v-bind:items="persons"
                                    v-model="form.contractor.id"
                                    :rules="validation.required"
                                    autocomplete
                                    :no-data-text="$t('message.warning.noData')"
                                >
                                </v-select>
                            </v-flex>
                        </v-layout>
                        <v-layout row>
                            <v-flex xs3 lg2>
                                <label class="form-label"
                                    >{{ $t("label.contractorManager") }}</label
                                >
                            </v-flex>
                            <v-flex xs9 lg10>
                                <v-select
                                    :disabled="disabled || noOfficeDisabled"
                                    v-bind:items="contractorManagers"
                                    v-model="form.contractorManager.id"
                                    autocomplete
                                    :no-data-text="$t('message.warning.noData')"
                                >
                                </v-select>
                            </v-flex>
                        </v-layout>
                        <v-layout row>
                            <v-flex xs3 lg2>
                                <label class="form-label"
                                    >{{ $t("label.description") }}*</label
                                >
                            </v-flex>
                            <v-flex xs9 lg10>
                                <v-text-field
                                    multi-line
                                    :disabled="disabled"
                                    v-model="form.description"
                                    :rules="validation.text"
                                    auto-grow
                                    :counter="
                                        constants.LENGTH_SYSTEM_DESCRIPTION
                                    "
                                    :maxlength="
                                        constants.LENGTH_SYSTEM_DESCRIPTION
                                    "
                                ></v-text-field>
                            </v-flex>
                        </v-layout>

                        <div class="subheading">
                            {{ $t("label.approvers") }}
                        </div>
                        <p v-html="$t('message.info.systemApprovers')"></p>

                        <app-approver
                            v-for="approver in form.approvers"
                            :key="approver.uuid"
                            :office="form.office.id"
                            :model="approver"
                            :list="form.approvers"
                            :disabled="disabled || form.isInUse"
                            :isSub="approver.isSub"
                            :msgAlt="approver.msgAlt"
                            v-on:remove="removeApprover"
                            v-on:change="updateApprover"
                            v-on:sub="addSub"
                        ></app-approver>
                    </v-card-text>

                    <v-card-actions v-if="!disabled">
                        <v-btn
                            :disabled="removing"
                            :loading="saving"
                            @click="save"
                            color="primary"
                        >
                            {{ $t("label.save") }}
                        </v-btn>
                        <v-btn
                            :disabled="saving || removing"
                            @click="addApprover"
                        >
                            <v-icon class="mr-3">person_outline</v-icon>
                            {{ $t("label.addApprover") }}
                        </v-btn>
                        <v-spacer></v-spacer>
                        <v-btn
                            :disabled="saving"
                            :loading="removing"
                            @click="remove"
                            color="error"
                            v-if="!form.isInUse && !!form.id"
                        >
                            {{ $t("label.remove") }}
                        </v-btn>
                    </v-card-actions>
                </v-card>
                <v-card v-else>
                    <app-card-loader></app-card-loader>
                </v-card>
            </v-form>
        </v-flex>
    </v-layout>

</template>

<script>
import constants from "@/helpers/constants";
import utilities from "@/helpers/utilities";
import validation from "@/helpers/validation";
import CardLoader from "@/components/shared/CardLoader";
import System from "@/helpers/entities/System";
import confirmDialogue from '@/components/shared/ConfirmDialogue';
import Approver from "./Approver";


export default {
    data() {
        return {
            constants,
            validation,
            currentApprovers: [],
            saving: false,
            loading: true,
            removing: false,

            areas: this.$store.getters.areasValueText,
            offices: this.$store.getters.officesValueText,
            // persons: this.$store.getters.personsHiredValueText,
            suppliers: this.$store.getters.suppliersValueText,

            form: System.build()
        };
    },

    computed: {
        contracts() {
            const cFilter = this.$store.getters.contracts
                            .filter(contract => contract.office.id === this.form.office.id);

            const cFormat = cFilter.map(({ id, number, description, isActive, validityTerm }) => ({
                value: id,
                text: `${number} - ${utilities.toDate(validityTerm.finish)} - ${description}`,
                isActive,
            }));
            return utilities.combobox(
                cFormat,
                this.form.contract.id
            );
        },
        contractItems() {
            return this.$store.getters.contractItemsValueText.filter(
                item => item.contractId === this.form.contract.id
            );
        },
        disabled() {
            return (
                (!this.$store.getters.profile.isAdmin &&
                    !this.$store.getters.profile.isRoot) ||
                !this.form.isActive
            );
        },
        noOfficeDisabled() {
            return !this.form.office.id;
        },
        persons() {
            if (!this.form.supplier.id || this.form.supplier.id === 1) {
                return this.$store.getters.personsHiredValueText;
            }

            const filteredPersons = this.$store.getters.persons.filter(
                item => !!item.office && item.office.id === this.form.office.id
            );

            return this.$store.getters.personsValueText.filter(person =>
                filteredPersons.find(el => el.id === person.value)
            );
        },
        contractorManagers() {
            return [
                { value: null, text: "Sem gerente Relacionado" },
                ...this.persons
            ];
        }
    },

    components: {
        appApprover: Approver,
        appCardLoader: CardLoader,
        confirmDialogue
    },

    watch: {
        $route: "fetch"
    },
    mounted() {
        setTimeout(() => {
            this.currentApprovers = this.form.approvers.slice(0, this.form.approvers.length);
        }, 1000);
    },
    created() {
        this.fetch();
    },

    methods: {
        addApprover() {
            this.form.approvers.splice(
                this.form.approvers.length,
                0,
                System.approver.build()
            );
        },
        removeApprover(uuid) {
            const index = this.form.approvers
                .map(approver => approver.uuid)
                .indexOf(uuid);
            this.form.approvers.splice(index, 1);
        },

        updateApprover(value) {
            if (this.currentApprovers.length > 0 && !this.disabled) {
                const cIndex = this.currentApprovers
                .map(approver => approver.uuid)
                .indexOf(value.uuid);

                if (cIndex > -1) {
                    value.msgAlt = "";
                    value.IsAlteraTodosSecretaria = false;
                    if (this.currentApprovers[cIndex].person.id !== value.person.id
                            && !this.currentApprovers[cIndex].isSub) {
                        this.$refs.confirmDialogue.show({
                            title: 'Confirmação',
                            message: 'Deseja alterar o aprovador em todos os sistemas cadastrados?',
                            okButton: 'Sim',
                            cancelButton: 'Não'
                        }).then((ok) => {
                            if (ok) {
                                value.msgAlt = "a alteração será realizada em todos os sistemas cadastrados";
                                value.IsAlteraTodosSecretaria = true;
                            } else {
                                value.msgAlt = "a alteração será realizada apenas neste sistema";
                            }
                        });
                    }
                }
            }
            const index = this.form.approvers
                .map(approver => approver.uuid)
                .indexOf(value.uuid);
            this.form.approvers.splice(index, 1, value);
        },
        addSub(base) {
            const index = this.form.approvers
                .map(approver => approver.uuid)
                .indexOf(base.uuid);
            const sub = System.approver.build();
            sub.type = base.type;
            sub.isSub = true;
            this.form.approvers.splice(index + 1, 0, sub);
        },

        clearContractor() {
            this.form.contractor.id = null;
            this.form.contractorManager.id = null;
        },

        async fetch() {
            this.loading = true;
            this.form.office.id = this.$store.getters.profile.office || null;
            const id = this.$route.params.id;
            if (!utilities.isEmptyOrZero(id)) {
                await this.$store.dispatch("fetchSystem", id);
                this.form = System.toForm(this.$store.getters.system);
                if (!this.form.contractorManager) {
                    this.form.contractorManager = {
                        id: null
                    };
                }
                if (utilities.isEmptyOrZero(this.form.contractorManager.id)) {
                    this.form.contractorManager.id = null;
                }
            }

            this.loading = false;
        },

        async remove() {
            this.removing = true;
            await this.$store.dispatch(
                "removeSystem",
                System.toPayload(this.form)
            );
            this.$router.push({ name: "systemIndex" });
        },

        async save() {
            if (!this.$refs.form.validate()) return;
            try {
                this.saving = true;
                await this.$store.dispatch(
                    "saveOrUpdateSystem",
                    System.toPayload(this.form)
                );
                this.$router.push({ name: "systemIndex" });
            } catch (error) {
                if (error.data && error.data.code === "generic_error") {
                    this.saving = false;
                } else {
                    throw error;
                }
            }
        }
    }
};
</script>



// WEBPACK FOOTER //
// src/components/systems/Edit.vue